export type UxMetricName = 'action_to_first_frame_ms' | 'request_duration_ms' | 'render_duration_ms';

export type UxMetricLabels = Readonly<Record<string, string>>;

export interface UxMetricEvent {
  readonly name: UxMetricName;
  readonly durationMs: number;
  readonly labels: UxMetricLabels;
}

export interface UxMetricsPort {
  readonly record: (event: UxMetricEvent) => void | PromiseLike<void>;
}

export interface MemoryUxMetricsPort {
  readonly port: UxMetricsPort;
  readonly events: () => readonly UxMetricEvent[];
  readonly clear: () => void;
}

export interface UxActionMarker {
  readonly startedAtMs: number;
}

export type UxNow = () => number;

type UxRequestResult = 'success' | 'failure';

const defaultNow: UxNow = () => performance.now();

export function createMemoryUxMetricsPort(): MemoryUxMetricsPort {
  let recordedEvents: UxMetricEvent[] = [];

  return {
    port: {
      record: (event) => {
        recordedEvents.push(copyEvent(event));
      }
    },
    events: () => recordedEvents.map(copyEvent),
    clear: () => {
      recordedEvents = [];
    }
  };
}

export function createNoopUxMetricsPort(): UxMetricsPort {
  return {
    record: () => {}
  };
}

export function measureUxRequest<T>(
  metrics: UxMetricsPort,
  request: string,
  labels: UxMetricLabels,
  operation: () => Promise<T>,
  now?: UxNow
): Promise<T>;
export function measureUxRequest<T>(
  metrics: UxMetricsPort,
  request: string,
  labels: UxMetricLabels,
  operation: () => T,
  now?: UxNow
): T;
export function measureUxRequest<T>(
  metrics: UxMetricsPort,
  request: string,
  labels: UxMetricLabels,
  operation: () => T | Promise<T>,
  now: UxNow = defaultNow
): T | Promise<T> {
  const startedAtMs = now();

  try {
    const result = operation();

    if (isPromiseLike(result)) {
      return result.then(
        (value) => {
          recordUxRequestDuration(metrics, startedAtMs, request, labels, 'success', now);
          return value;
        },
        (error) => {
          recordUxRequestDuration(metrics, startedAtMs, request, labels, 'failure', now);
          throw error;
        }
      );
    }

    recordUxRequestDuration(metrics, startedAtMs, request, labels, 'success', now);
    return result;
  } catch (error) {
    recordUxRequestDuration(metrics, startedAtMs, request, labels, 'failure', now);
    throw error;
  }
}

export function measureUxRender<T>(
  metrics: UxMetricsPort,
  screen: string,
  operation: () => Promise<T>,
  now?: UxNow
): Promise<T>;
export function measureUxRender<T>(metrics: UxMetricsPort, screen: string, operation: () => T, now?: UxNow): T;
export function measureUxRender<T>(
  metrics: UxMetricsPort,
  screen: string,
  operation: () => T | Promise<T>,
  now: UxNow = defaultNow
): T | Promise<T> {
  const startedAtMs = now();

  try {
    const result = operation();

    if (isPromiseLike(result)) {
      return result.then(
        (value) => {
          recordUxRenderDuration(metrics, startedAtMs, screen, now);
          return value;
        },
        (error) => {
          recordUxRenderDuration(metrics, startedAtMs, screen, now);
          throw error;
        }
      );
    }

    recordUxRenderDuration(metrics, startedAtMs, screen, now);
    return result;
  } catch (error) {
    recordUxRenderDuration(metrics, startedAtMs, screen, now);
    throw error;
  }
}

export function markUxActionStart(now: UxNow = defaultNow): UxActionMarker {
  return {
    startedAtMs: now()
  };
}

export function recordUxActionFirstFrame(
  metrics: UxMetricsPort,
  marker: UxActionMarker,
  labels: UxMetricLabels,
  now: UxNow = defaultNow
): void {
  recordMetric(metrics, 'action_to_first_frame_ms', now() - marker.startedAtMs, labels);
}

function recordUxRequestDuration(
  metrics: UxMetricsPort,
  startedAtMs: number,
  request: string,
  labels: UxMetricLabels,
  result: UxRequestResult,
  now: UxNow
): void {
  recordMetric(metrics, 'request_duration_ms', now() - startedAtMs, {
    ...labels,
    request,
    result
  });
}

function recordUxRenderDuration(metrics: UxMetricsPort, startedAtMs: number, screen: string, now: UxNow): void {
  recordMetric(metrics, 'render_duration_ms', now() - startedAtMs, { screen });
}

function recordMetric(metrics: UxMetricsPort, name: UxMetricName, durationMs: number, labels: UxMetricLabels): void {
  try {
    const result = metrics.record({
      name,
      durationMs,
      labels: copyLabels(labels)
    });

    if (isPromiseLike(result)) {
      void Promise.resolve(result).catch(ignoreMetricFailure);
    }
  } catch {
    ignoreMetricFailure();
  }
}

function ignoreMetricFailure(): void {
  // Metrics failures must not block business operations.
}

function copyEvent(event: UxMetricEvent): UxMetricEvent {
  return {
    name: event.name,
    durationMs: event.durationMs,
    labels: copyLabels(event.labels)
  };
}

function copyLabels(labels: UxMetricLabels): UxMetricLabels {
  return { ...labels };
}

function isPromiseLike<T>(value: T | PromiseLike<T>): value is PromiseLike<T> {
  return (
    value !== null &&
    (typeof value === 'object' || typeof value === 'function') &&
    'then' in value &&
    typeof value.then === 'function'
  );
}
